import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";

import { LojaPageContainer, LojaDataGrid } from "layouts/pages/loja/common";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { setMessage, useMaterialUIController } from "context";
import { formatDate } from "helpers/formatDate";
import { IEventoResponse } from "interfaces/eventos";
import {
  CANAL_LABEL,
  ComunicadoCampanhaStatus,
  IComunicadoCampanha,
  PUBLICO_LABEL,
  STATUS_LABEL,
} from "interfaces/comunicado";
import { getAllEventosPeridoByActive } from "services/evento";
import { listarComunicados } from "services/comunicados";
import ComunicadoComposerDrawer from "./components/ComunicadoComposerDrawer";
import ComunicadoDetalheDrawer from "./components/ComunicadoDetalheDrawer";
import { COMUNICADO_ACCENT } from "./components/comunicado-composer.theme";

const PENDING_STATUSES: ComunicadoCampanhaStatus[] = ["ENFILEIRADO", "PROCESSANDO"];
const POLL_INTERVAL_MS = 3000;

const STATUS_FILTER: { value: "" | ComunicadoCampanhaStatus; label: string }[] =
  [
    { value: "", label: "Todos os status" },
    { value: "ENFILEIRADO", label: "Enfileirado" },
    { value: "PROCESSANDO", label: "Processando" },
    { value: "CONCLUIDO", label: "Concluído" },
    { value: "ERRO", label: "Erro" },
    { value: "CANCELADO", label: "Cancelado" },
  ];

function statusColor(status: ComunicadoCampanhaStatus) {
  switch (status) {
    case "CONCLUIDO":
      return "success";
    case "PROCESSANDO":
    case "ENFILEIRADO":
      return "info";
    case "ERRO":
      return "error";
    case "CANCELADO":
      return "default";
    default:
      return "warning";
  }
}

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? COMUNICADO_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Comunicados() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<IComunicadoCampanha[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"" | ComunicadoCampanhaStatus>("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [eventos, setEventos] = useState<IEventoResponse[]>([]);

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (!silent) setLoading(true);
      try {
        const data = await listarComunicados({
          page: 1,
          limit: 50,
          status: statusFilter || undefined,
        });
        setRows(data.items);
      } catch (error: unknown) {
        if (!silent) {
          setMessage(dispatch, {
            open: true,
            severity: "error",
            text:
              error instanceof Error
                ? error.message
                : "Falha ao carregar campanhas.",
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dispatch, statusFilter],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const hasPendingCampaigns = useMemo(
    () => rows.some((row) => PENDING_STATUSES.includes(row.status)),
    [rows],
  );

  useEffect(() => {
    if (!hasPendingCampaigns) return;

    const interval = window.setInterval(() => {
      void load({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [hasPendingCampaigns, load]);

  useEffect(() => {
    getAllEventosPeridoByActive(1, 200)
      .then((res) => setEventos(res.eventos ?? []))
      .catch(() => setEventos([]));
  }, []);

  const stats = useMemo(() => {
    const enviados = rows.reduce((s, r) => s + r.totalEnviados, 0);
    const falhas = rows.reduce((s, r) => s + r.totalFalhas, 0);
    const emFila = rows.filter((r) => PENDING_STATUSES.includes(r.status)).length;
    return { total: rows.length, enviados, falhas, emFila };
  }, [rows]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "tituloInterno",
        headerName: "Campanha",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "canal",
        headerName: "Canal",
        width: 130,
        valueGetter: (params) =>
          CANAL_LABEL[params.row.canal as keyof typeof CANAL_LABEL],
      },
      {
        field: "publico",
        headerName: "Público",
        width: 170,
        valueGetter: (params) =>
          PUBLICO_LABEL[params.row.publico as keyof typeof PUBLICO_LABEL],
      },
      {
        field: "evento",
        headerName: "Evento",
        width: 160,
        valueGetter: (params) => params.row.evento?.titulo ?? "—",
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STATUS_LABEL[params.value as ComunicadoCampanhaStatus]}
            color={
              statusColor(params.value as ComunicadoCampanhaStatus) as
                | "success"
                | "info"
                | "error"
                | "warning"
                | "default"
            }
          />
        ),
      },
      {
        field: "totalEnviados",
        headerName: "Enviados",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "totalFalhas",
        headerName: "Falhas",
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "createAt",
        headerName: "Criado em",
        width: 140,
        valueFormatter: (params) =>
          params.value
            ? formatDate(params.value as string, "dd/MM/yyyy HH:mm")
            : "",
      },
    ],
    [],
  );

  const handleRowClick = (params: GridRowParams<IComunicadoCampanha>) => {
    setSelectedId(params.row.id);
    setDetalheOpen(true);
  };

  return (
    <LojaPageContainer
      title="Comunicados"
      subtitle="Campanhas de e-mail e push para atletas e usuários da plataforma."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => setComposerOpen(true)}
          sx={{
            background: `linear-gradient(195deg, ${COMUNICADO_ACCENT}, #e04a1a)`,
          }}
        >
          <Icon sx={{ mr: 0.5 }}>add</Icon>
          Nova campanha
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini label="Campanhas listadas" value={stats.total} accent />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Envios concluídos" value={stats.enviados} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Falhas registradas" value={stats.falhas} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Em fila / processando" value={stats.emFila} />
        </Grid>
      </Grid>

      <MDBox
        mb={2}
        p={2}
        borderRadius="lg"
        sx={{
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          borderLeft: `4px solid ${COMUNICADO_ACCENT}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CampaignOutlinedIcon sx={{ color: COMUNICADO_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              Histórico de campanhas
            </MDTypography>
          </Stack>
          <Autocomplete
            options={STATUS_FILTER}
            value={STATUS_FILTER.find((o) => o.value === statusFilter) ?? STATUS_FILTER[0]}
            getOptionLabel={(o) => o.label}
            onChange={(_, v) => setStatusFilter(v?.value ?? "")}
            sx={{ minWidth: { xs: "100%", sm: 240 } }}
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Filtrar status" />
            )}
          />
        </Stack>
      </MDBox>

      {!loading && rows.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <CampaignOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhuma campanha ainda
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            Crie a primeira campanha para avisar inscritos sobre kit, horários ou
            novidades do evento.
          </MDTypography>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => setComposerOpen(true)}
            sx={{
              background: `linear-gradient(195deg, ${COMUNICADO_ACCENT}, #e04a1a)`,
            }}
          >
            Criar campanha
          </MDButton>
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          sx={{ cursor: "pointer" }}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          disableRowSelectionOnClick
        />
      )}

      <ComunicadoComposerDrawer
        open={composerOpen}
        eventos={eventos}
        onClose={() => setComposerOpen(false)}
        onCreated={() => void load()}
      />

      <ComunicadoDetalheDrawer
        open={detalheOpen}
        campanhaId={selectedId}
        onClose={() => {
          setDetalheOpen(false);
          setSelectedId(null);
        }}
        onUpdated={() => void load()}
      />
    </LojaPageContainer>
  );
}
